import { Suspense, useEffect, useState, useTransition } from "react";

import { useQueryLoader } from "react-relay";

import { templateInitialSearchResultsRelayQuery } from "@/__generated__/templateInitialSearchResultsRelayQuery.graphql";
import { templatePaginatedSearchResultsRelayRefetchableFragment$key } from "@/__generated__/templatePaginatedSearchResultsRelayRefetchableFragment.graphql";
import { templateRecommendationsSearchResultsRelayQuery } from "@/__generated__/templateRecommendationsSearchResultsRelayQuery.graphql";
import { SkeletonTemplateRecommendationCardGrid } from "@/components/confluence/templates/skeleton-template-card-grid";

import {
    CategorySearchSuggestion,
    TemplateCategorySearchResults
} from "../components/template-category-search-results-relay";
import {
    isCategoryType,
    isSearchType,
    isTemplateType,
    TemplateFuzzySearchResults,
    TemplateSearchSuggestion
} from "../components/template-fuzzy-search-results-relay";
import {
    parseSearchQuery,
    TemplateInitialSearchResultsRelayQuery
} from "../components/template-initial-search-results-relay";
import {
    getTemplateRecommendationsSearchResultsQueryVars,
    TemplateRecommendationsSearchResults,
    TemplateRecommendationsSearchResultsRelayQuery
} from "../components/template-recommendations-search-results-relay";
import { DEFAULT_CATEGORY_SLUG } from "../hooks/use-category-slug";
import { useQueryLocale } from "../hooks/use-query-locale";

export type TemplateSearchResultsSectionProps = {
    preloadedSearchResults?: templatePaginatedSearchResultsRelayRefetchableFragment$key | null;
    searchTerm:
        | string
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | null;
    searchIntent?:
        | string
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | null;
};

const CATEGORY = "category";
const SEARCH = "search";
const RECOMMENDED = "recommended";

/**
 * Create a unique key to identify the search term.
 * This is used to force a new instance
 * of `TemplatePaginatedSearchResults`
 * when the search term changes.
 */
const getSearchKey = (
    searchTerm:
        | string
        | TemplateSearchSuggestion
        | CategorySearchSuggestion
        | null
): string => {
    if (isCategoryType(searchTerm)) {
        return searchTerm.id;
    }
    if (isTemplateType(searchTerm)) {
        return searchTerm.id;
    }
    if (isSearchType(searchTerm)) {
        return searchTerm;
    }
    return RECOMMENDED;
};

type TemplateSearchResultsState =
    | {
          type: typeof CATEGORY;
          key: string;
          term: CategorySearchSuggestion;
          hasPreloadedSearchResults: boolean;
      }
    | {
          type: typeof SEARCH;
          key: string;
          term: string | TemplateSearchSuggestion;
      }
    | { type: typeof RECOMMENDED; key: string }
    | { type: null };

const getInitialState = (
    searchTerm: TemplateSearchResultsSectionProps["searchTerm"],
    preloadedSearchResults: TemplateSearchResultsSectionProps["preloadedSearchResults"]
): TemplateSearchResultsState => {
    if (preloadedSearchResults != null && isCategoryType(searchTerm)) {
        return {
            type: CATEGORY,
            key: getSearchKey(searchTerm),
            term: searchTerm,
            hasPreloadedSearchResults: true
        };
    }
    return { type: null };
};

const getNextState = (
    state: TemplateSearchResultsState,
    searchTerm: TemplateSearchResultsSectionProps["searchTerm"]
): TemplateSearchResultsState => {
    const newKey = getSearchKey(searchTerm);
    if (state.type == null || newKey !== state?.key) {
        if (isCategoryType(searchTerm)) {
            return {
                type: CATEGORY,
                key: newKey,
                term: searchTerm,
                hasPreloadedSearchResults: false
            };
        } else if (isTemplateType(searchTerm) || isSearchType(searchTerm)) {
            return {
                type: SEARCH,
                key: newKey,
                term: searchTerm
            };
        } else {
            return {
                key: newKey,
                type: RECOMMENDED
            };
        }
    }
    return state;
};

export const TemplateSearchResultsSection = ({
    preloadedSearchResults,
    searchTerm,
    searchIntent
}: TemplateSearchResultsSectionProps) => {
    const [searchTypePending, startSearchTypeTransition] = useTransition();
    const locale = useQueryLocale();

    const [state, setState] = useState(() =>
        getInitialState(searchTerm, preloadedSearchResults)
    );

    const [, preloadSearchIntent] =
        useQueryLoader<templateInitialSearchResultsRelayQuery>(
            TemplateInitialSearchResultsRelayQuery
        );

    const [, preloadRecommendations] =
        useQueryLoader<templateRecommendationsSearchResultsRelayQuery>(
            TemplateRecommendationsSearchResultsRelayQuery
        );

    useEffect(() => {
        if (state.type == null || getSearchKey(searchTerm) !== state.key) {
            startSearchTypeTransition(() => {
                setState((prevState) => getNextState(prevState, searchTerm));
            });
        }
    }, [searchTerm, state]);

    useEffect(() => {
        if (searchIntent && isCategoryType(searchIntent)) {
            if (searchIntent.id == DEFAULT_CATEGORY_SLUG) {
                preloadRecommendations(
                    getTemplateRecommendationsSearchResultsQueryVars(location)
                );
            } else {
                preloadSearchIntent({
                    searchQuery: parseSearchQuery(searchIntent, {
                        withLocales: [locale]
                    })
                });
            }
        }
    }, [locale, preloadRecommendations, preloadSearchIntent, searchIntent]);

    return searchTypePending ? (
        <SkeletonTemplateRecommendationCardGrid />
    ) : (
        <Suspense fallback={<SkeletonTemplateRecommendationCardGrid />}>
            {state.type === RECOMMENDED ? (
                <TemplateRecommendationsSearchResults />
            ) : state.type === SEARCH ? (
                <TemplateFuzzySearchResults
                    searchKey={state.key}
                    searchTerm={state.term}
                />
            ) : state.type === CATEGORY ? (
                <TemplateCategorySearchResults
                    searchKey={state.key}
                    searchTerm={state.term}
                    preloadedSearchResults={
                        state.hasPreloadedSearchResults
                            ? preloadedSearchResults
                            : null
                    }
                />
            ) : (
                <SkeletonTemplateRecommendationCardGrid />
            )}
        </Suspense>
    );
};
